<script lang="ts" setup>
  import { cva } from 'class-variance-authority'

  const selectedValue = defineModel<boolean | string[]>()

  interface Props {
    id?: string
    label?: string
    value?: string
    hasError?: boolean
    disabled?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    id: '',
    label: '',
    value: '',
    hasError: false,
    disabled: false
  })

  const labelId = `${useId()}-label`

  const checkboxState = computed<boolean>({
    get() {
      if (Array.isArray(selectedValue.value)) {
        return selectedValue.value.includes(props.value)
      }

      return !!selectedValue.value
    },
    set(val) {
      if (Array.isArray(selectedValue.value)) {
        const hasValue = selectedValue.value.includes(props.value)

        if (val && !hasValue) {
          selectedValue.value = [...selectedValue.value, props.value]
        }

        if (!val && hasValue) {
          selectedValue.value = selectedValue.value.filter((item) => item !== props.value)
        }

        return
      }

      selectedValue.value = val
    }
  })

  const checkboxBase = computed(() =>
    cva(
      'flex h-6 w-6 flex-shrink-0 flex-grow-0 appearance-none items-center justify-center rounded border md:hover:border-bt-blue-ui',
      {
        variants: {
          default: {
            true: 'bg-white border-border',
            false: ''
          },
          disabled: {
            true: 'bg-grey border-none',
            false: ''
          },
          error: {
            true: 'bg-white border-dark-error',
            false: ''
          }
        }
      }
    )({
      default: !props.disabled && !props.hasError,
      disabled: props.disabled,
      error: props.hasError
    })
  )

  watch(
    selectedValue,
    (newValue) => {
      if (Array.isArray(newValue)) {
        checkboxState.value = newValue.includes(props.value)
        return
      }

      checkboxState.value = !!newValue
    },
    { deep: true }
  )

  watch(checkboxState, (checked) => {
    if (Array.isArray(selectedValue.value)) {
      const selectedValues = selectedValue.value
      const valueIndex = selectedValues.indexOf(props.value)

      if (checked) {
        if (valueIndex === -1) {
          selectedValues.push(props.value)
        }
        return
      }

      if (valueIndex !== -1) {
        selectedValues.splice(valueIndex, 1)
      }
      return
    }

    selectedValue.value = checked
  })

  const onLabelClick = (event: MouseEvent) => {
    const target = event.target
    if (!(target instanceof Element)) return

    const interactiveElement = target.closest('a, button, input, select, textarea, [role="button"], [role="link"]')
    if (interactiveElement) return

    const label = event.currentTarget as HTMLElement
    const checkbox = label.parentElement?.querySelector<HTMLButtonElement>('[role="checkbox"]')
    checkbox?.focus()
    checkbox?.click()
  }
</script>
<template>
  <div class="flex w-full min-w-0 flex-row items-center gap-4">
    <CheckboxRoot
      :id="id || undefined"
      v-model:checked="checkboxState"
      :aria-labelledby="$slots.label || label ? labelId : undefined"
      :disabled="disabled"
      :class="checkboxBase"
      data-test="checkbox"
      class="shrink-0"
    >
      <CheckboxIndicator
        class="rounded"
        :class="disabled ? 'bg-grey text-border' : 'bg-bt-blue-ui text-white'"
      >
        <Icon name="common/round-check" />
      </CheckboxIndicator>
    </CheckboxRoot>

    <span
      v-if="$slots.label || label"
      :id="labelId"
      data-test="checkbox-label"
      class="min-w-0 select-none text-body-1"
      :class="disabled ? 'cursor-default' : 'cursor-pointer'"
      @click="onLabelClick"
    >
      <slot name="label">
        {{ label }}
      </slot>
    </span>
  </div>
</template>
